import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { mainSiteUrl } from '@/mocks/categories';
import { SITE_URL } from '@/lib/site';

export default function Article() {
  const { t } = useTranslation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  const toc = [
    { id: 'what-is', label: t('section1.title') },
    { id: 'how-organized', label: t('section2.title') },
    { id: 'what-find', label: t('section3.title') },
    { id: 'browse-safely', label: t('section4.title') },
    { id: 'why-use', label: t('section5.title') },
  ];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: t('h1'),
    description: t('meta.description'),
    dateModified: '2026-08-20',
    inLanguage: 'en',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${SITE_URL}/article`,
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: t('breadcrumb.home'),
        item: `${SITE_URL}/`,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: t('breadcrumb.article'),
        item: `${SITE_URL}/article`,
      },
    ],
  };

  return (
    <div className="min-h-screen bg-cream">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <Navbar />

      <main className="pt-16">
        <div className="mx-auto w-full max-w-4xl px-4 py-10 md:px-6 md:py-14">
          {/* Breadcrumb */}
          <nav className="flex flex-wrap items-center gap-1.5 text-sm text-stone-500">
            <Link to="/" className="transition-colors hover:text-emerald-600">
              {t('breadcrumb.home')}
            </Link>
            <span className="flex h-4 w-4 items-center justify-center text-xs text-stone-300">
              <i className="ri-arrow-right-s-line"></i>
            </span>
            <span className="text-stone-400">{t('breadcrumb.article')}</span>
          </nav>

          {/* Header */}
          <header className="mt-6">
            <span className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
              <i className="ri-article-line"></i>
              USFans Spreadsheet
            </span>
            <h1 className="mt-4 font-heading text-2xl font-extrabold leading-tight text-stone-900 md:text-4xl">
              {t('h1')}
            </h1>
            <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-stone-400">
              <span className="inline-flex items-center gap-1.5">
                <span className="flex h-4 w-4 items-center justify-center text-sm">
                  <i className="ri-calendar-line"></i>
                </span>
                {t('updated')}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="flex h-4 w-4 items-center justify-center text-sm">
                  <i className="ri-time-line"></i>
                </span>
                {t('readingTime')}
              </span>
            </div>
          </header>

          <p className="mt-6 text-base leading-relaxed text-stone-600 md:text-lg">{t('intro')}</p>

          {/* Table of Contents */}
          <nav className="mt-8 rounded-lg border border-stone-200 bg-white p-5">
            <h2 className="font-heading text-sm font-semibold uppercase tracking-wide text-stone-900">
              {t('toc.title')}
            </h2>
            <ul className="mt-3 space-y-2">
              {toc.map((item) => (
                <li key={item.id}>
                  <a
                    href={`#${item.id}`}
                    className="inline-flex items-center gap-2 text-sm text-stone-600 transition-colors hover:text-emerald-600"
                  >
                    <span className="flex h-4 w-4 items-center justify-center text-xs text-emerald-500">
                      <i className="ri-arrow-right-s-line"></i>
                    </span>
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          {/* Section 1 */}
          <section id="what-is" className="mt-10">
            <h2 className="font-heading text-xl font-bold text-stone-900 md:text-2xl">
              {t('section1.title')}
            </h2>
            <p className="mt-4 text-base leading-relaxed text-stone-600">{t('section1.body1')}</p>
            <p className="mt-4 text-base leading-relaxed text-stone-600">{t('section1.body2')}</p>
          </section>

          {/* Section 2 */}
          <section id="how-organized" className="mt-10">
            <h2 className="font-heading text-xl font-bold text-stone-900 md:text-2xl">
              {t('section2.title')}
            </h2>
            <p className="mt-4 text-base leading-relaxed text-stone-600">{t('section2.body1')}</p>
            <ul className="mt-5 space-y-3">
              {['list1', 'list2', 'list3', 'list4', 'list5'].map((key) => (
                <li key={key} className="flex items-start gap-3">
                  <span className="mt-1.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-xs text-emerald-600">
                    <i className="ri-check-line"></i>
                  </span>
                  <span className="text-sm leading-relaxed text-stone-600 md:text-base">
                    {t(`section2.${key}`)}
                  </span>
                </li>
              ))}
            </ul>
            <p className="mt-5 text-base leading-relaxed text-stone-600">{t('section2.body2')}</p>
          </section>

          {/* Section 3 */}
          <section id="what-find" className="mt-10">
            <h2 className="font-heading text-xl font-bold text-stone-900 md:text-2xl">
              {t('section3.title')}
            </h2>
            <p className="mt-4 text-base leading-relaxed text-stone-600">{t('section3.body1')}</p>

            <div className="mt-6 overflow-hidden rounded-lg border border-stone-200 bg-white">
              <div className="border-b border-stone-100 px-4 py-3 text-sm font-medium text-stone-500">
                {t('table.caption')}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[480px] text-left text-sm">
                  <thead>
                    <tr className="bg-stone-50 text-xs uppercase tracking-wide text-stone-500">
                      <th className="px-4 py-3 font-medium">{t('table.colCategory')}</th>
                      <th className="px-4 py-3 font-medium">{t('table.colPurpose')}</th>
                      <th className="px-4 py-3 font-medium">{t('table.colExample')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {['row1', 'row2', 'row3', 'row4'].map((row) => (
                      <tr key={row} className="border-t border-stone-100">
                        <td className="whitespace-nowrap px-4 py-3 font-medium text-stone-800">
                          {t(`table.${row}.cat`)}
                        </td>
                        <td className="px-4 py-3 text-stone-500">{t(`table.${row}.purpose`)}</td>
                        <td className="whitespace-nowrap px-4 py-3 text-stone-500">
                          {t(`table.${row}.example`)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          {/* Section 4 */}
          <section id="browse-safely" className="mt-10">
            <h2 className="font-heading text-xl font-bold text-stone-900 md:text-2xl">
              {t('section4.title')}
            </h2>
            <p className="mt-4 text-base leading-relaxed text-stone-600">{t('section4.body1')}</p>
            <ul className="mt-5 space-y-3">
              {['tip1', 'tip2', 'tip3', 'tip4', 'tip5'].map((key) => (
                <li key={key} className="flex items-start gap-3">
                  <span className="mt-1.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-amber-50 text-xs text-amber-600">
                    <i className="ri-alert-line"></i>
                  </span>
                  <span className="text-sm leading-relaxed text-stone-600 md:text-base">
                    {t(`section4.${key}`)}
                  </span>
                </li>
              ))}
            </ul>
            <p className="mt-5 text-base leading-relaxed text-stone-600">{t('section4.body2')}</p>
          </section>

          {/* Section 5 */}
          <section id="why-use" className="mt-10">
            <h2 className="font-heading text-xl font-bold text-stone-900 md:text-2xl">
              {t('section5.title')}
            </h2>
            <p className="mt-4 text-base leading-relaxed text-stone-600">{t('section5.body1')}</p>
            <p className="mt-4 text-base leading-relaxed text-stone-600">{t('section5.body2')}</p>
          </section>

          {/* FAQ */}
          <section className="mt-12">
            <h2 className="font-heading text-xl font-bold text-stone-900 md:text-2xl">
              {t('faq.title')}
            </h2>
            <ArticleFaq />
          </section>

          {/* CTA */}
          <section className="mt-12 rounded-lg bg-emerald-50 p-8 text-center">
            <h2 className="font-heading text-xl font-bold text-stone-900 md:text-2xl">
              {t('cta.title')}
            </h2>
            <p className="mx-auto mt-3 max-w-lg text-sm leading-relaxed text-stone-600 md:text-base">
              {t('cta.body')}
            </p>
            <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-emerald-600 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-emerald-700 sm:w-auto"
              >
                {t('cta.primary')}
                <i className="ri-external-link-line"></i>
              </a>
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md border border-emerald-300 bg-white px-6 py-3 text-sm font-semibold text-emerald-700 transition-colors hover:bg-emerald-50 sm:w-auto"
              >
                {t('cta.secondary')}
              </a>
            </div>
          </section>

          {/* Safety note */}
          <div className="mt-6 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
            <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center text-amber-600">
              <i className="ri-shield-check-line"></i>
            </span>
            <p className="text-xs leading-relaxed text-amber-800 md:text-sm">{t('safetyNote')}</p>
          </div>

          {/* Related resources */}
          <section className="mt-12">
            <h2 className="font-heading text-xl font-bold text-stone-900 md:text-2xl">
              {t('related.title')}
            </h2>
            <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Link
                to="/"
                className="group rounded-lg border border-stone-200 bg-white p-6 transition-colors hover:border-emerald-300"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-emerald-50 text-xl text-emerald-600">
                  <i className="ri-home-4-line"></i>
                </span>
                <h3 className="mt-4 font-heading text-base font-semibold text-stone-900">
                  {t('related.home')}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-stone-500">{t('related.homeDesc')}</p>
                <span className="mt-3 inline-flex items-center gap-1 whitespace-nowrap text-sm font-medium text-emerald-600">
                  {t('cta.backHome')}
                  <i className="ri-arrow-right-line"></i>
                </span>
              </Link>
              <Link
                to="/"
                className="group rounded-lg border border-stone-200 bg-white p-6 transition-colors hover:border-emerald-300"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-amber-50 text-xl text-amber-600">
                  <i className="ri-grid-line"></i>
                </span>
                <h3 className="mt-4 font-heading text-base font-semibold text-stone-900">
                  {t('related.categories')}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-stone-500">{t('related.categoriesDesc')}</p>
                <span className="mt-3 inline-flex items-center gap-1 whitespace-nowrap text-sm font-medium text-emerald-600">
                  {t('nav.categories')}
                  <i className="ri-arrow-right-line"></i>
                </span>
              </Link>
            </div>

            <div className="mt-8 text-center">
              <Link
                to="/"
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-stone-300 bg-white px-5 py-2.5 text-sm font-semibold text-stone-700 transition-colors hover:bg-stone-50"
              >
                <i className="ri-arrow-left-line"></i>
                {t('cta.backHome')}
              </Link>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}

function ArticleFaq() {
  const { t } = useTranslation();
  const [open, setOpen] = useState<number | null>(0);

  const faqs = Array.from({ length: 5 }, (_, i) => ({
    q: t(`faq.q${i + 1}`),
    a: t(`faq.a${i + 1}`),
  }));

  return (
    <div className="mt-5 space-y-3">
      {faqs.map((faq, i) => {
        const isOpen = open === i;
        return (
          <div key={i} className="overflow-hidden rounded-lg border border-stone-200 bg-white">
            <button
              type="button"
              onClick={() => setOpen(isOpen ? null : i)}
              className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left transition-colors hover:bg-stone-50 cursor-pointer"
            >
              <span className="text-sm font-semibold text-stone-800">{faq.q}</span>
              <span
                className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-base transition-colors ${
                  isOpen ? 'bg-emerald-600 text-white' : 'bg-stone-100 text-stone-500'
                }`}
              >
                <i className={isOpen ? 'ri-subtract-line' : 'ri-add-line'}></i>
              </span>
            </button>
            {isOpen && (
              <div className="px-5 pb-5">
                <p className="text-sm leading-relaxed text-stone-600">{faq.a}</p>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}